import { useCallback, useEffect } from 'react';

import {
  useTrackAgentElicitationRequested,
  useTrackAgentElicitationResponded,
  type WorkspaceAgentElicitationRequestedType,
  type WorkspaceAgentElicitationRespondedCta,
} from '@air/analytics';

import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import {
  WORKSPACE_AGENT_ELICITATION_STEP_INDEX,
  workspaceAgentElicitationComponentType,
  workspaceAgentElicitationDecision,
  workspaceAgentElicitationHasEscapeHatch,
  workspaceAgentElicitationSelectedOptionIds,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentElicitationAnalytics';

type WorkspaceAgentDockElicitationType = Exclude<WorkspaceAgentElicitationRequestedType, 'guided_flow'>;

type UseWorkspaceAgentElicitationTrackingParams = {
  createdAt: string;
  elicitationId: string;
  elicitationType: WorkspaceAgentDockElicitationType;
  fieldCount?: number;
  optionCount?: number;
  toolName?: string;
};

type TrackWorkspaceAgentElicitationResponseParams = {
  cta: WorkspaceAgentElicitationRespondedCta;
  rememberForSession?: boolean;
  selectedIndexes?: readonly number[];
  usedEscapeHatch?: boolean;
};

export const useWorkspaceAgentElicitationTracking = ({
  createdAt,
  elicitationId,
  elicitationType,
  fieldCount,
  optionCount,
  toolName,
}: UseWorkspaceAgentElicitationTrackingParams) => {
  const session = usePrivateWorkspaceAgentPanelSession();
  const chatId = session.sessionId;
  const { trackAgentElicitationRequested } = useTrackAgentElicitationRequested();
  const { trackAgentElicitationResponded } = useTrackAgentElicitationResponded();

  useEffect(() => {
    if (!chatId) {
      return;
    }

    trackAgentElicitationRequested({
      chat_id: chatId,
      component_type: workspaceAgentElicitationComponentType(elicitationType),
      elicitation_id: elicitationId,
      elicitation_type: elicitationType,
      has_escape_hatch: workspaceAgentElicitationHasEscapeHatch(elicitationType),
      step_index: WORKSPACE_AGENT_ELICITATION_STEP_INDEX,
      ...(fieldCount === undefined ? {} : { field_count: fieldCount }),
      ...(optionCount === undefined ? {} : { option_count: optionCount }),
      ...(toolName === undefined ? {} : { tool_name: toolName }),
    });
  }, [chatId, elicitationId, elicitationType, fieldCount, optionCount, toolName, trackAgentElicitationRequested]);

  return useCallback(
    ({
      cta,
      rememberForSession = false,
      selectedIndexes,
      usedEscapeHatch,
    }: TrackWorkspaceAgentElicitationResponseParams) => {
      if (!chatId) {
        return;
      }

      const createdAtMs = Date.parse(createdAt);
      const responseTimeMs =
        Number.isFinite(createdAtMs) && Date.now() >= createdAtMs ? Date.now() - createdAtMs : undefined;

      trackAgentElicitationResponded({
        chat_id: chatId,
        component_type: workspaceAgentElicitationComponentType(elicitationType),
        cta,
        decision: workspaceAgentElicitationDecision({ cta, elicitationType }),
        elicitation_id: elicitationId,
        elicitation_type: elicitationType,
        remember_for_session: rememberForSession,
        step_index: WORKSPACE_AGENT_ELICITATION_STEP_INDEX,
        ...(responseTimeMs === undefined
          ? {}
          : { response_time_ms: responseTimeMs, time_to_submit_ms: responseTimeMs }),
        ...(cta === 'approve'
          ? {
              used_escape_hatch: usedEscapeHatch ?? false,
              ...(selectedIndexes === undefined
                ? {}
                : {
                    selected_count: selectedIndexes.length,
                    selected_option_ids: workspaceAgentElicitationSelectedOptionIds(selectedIndexes),
                  }),
            }
          : {}),
        ...(toolName === undefined ? {} : { tool_name: toolName }),
      });
    },
    [chatId, createdAt, elicitationId, elicitationType, toolName, trackAgentElicitationResponded],
  );
};
